<script setup>
import { computed, onMounted, ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useDashboardStore } from '@/stores/dashboard.store'
import { useDetectDevice } from '@/composables/device'
import { SIGNAL_BAR_PAGE_METRICS } from '@/composables/dashboard-kpi'
import SignalMetric from '@/components/shared/signal-bar/SignalMetric.vue'

const props = defineProps({
  page: { type: String, required: true },
  filters: { type: Object, default: () => ({}) },
  embedded: { type: Boolean, default: false },
  visible: { type: Boolean, default: false },
})

const { t } = getI18n()
const webSiteStore = useWebSiteStore()
const dashboardStore = useDashboardStore()
const { isMobile } = useDetectDevice()

const metricKeys = computed(() =>
  (SIGNAL_BAR_PAGE_METRICS[props.page] ?? []).filter(key => dashboardStore.kpiMetric(key))
)

const hiddenPages = computed(() => webSiteStore.signalBarHiddenPages ?? [])
const mobileHidden = ref(true)
const hidden = computed(() =>
  isMobile.value ? mobileHidden.value : hiddenPages.value.includes(props.page)
)

const setHidden = (value) => {
  if (isMobile.value) {
    mobileHidden.value = value
    return
  }
  const rest = hiddenPages.value.filter(p => p !== props.page)
  webSiteStore.signalBarHiddenPages = value ? [...rest, props.page] : rest
}

onMounted(() => dashboardStore.fetchDashboardCatalogAsync())
</script>

<template>
  <div
    v-if="embedded && metricKeys.length"
    class="signal-bar-embedded-wrap"
    :style="{ overflow: 'hidden', maxHeight: visible ? '500px' : '0px', transition: 'max-height 0.5s' }"
  >
    <div class="signal-bar-embedded">
      <signal-metric
        v-for="key in metricKeys"
        :key="key"
        :metric-key="key"
        :filters="filters"
      />
    </div>
  </div>

  <div
    v-else-if="metricKeys.length"
    class="signal-bar"
    :class="{ 'signal-bar--collapsed': hidden, 'signal-bar--mobile': isMobile }"
  >
    <div class="signal-bar-drawer" :class="{ 'signal-bar-drawer--hidden': hidden }">
      <div class="signal-bar-track">
        <template v-for="(key, index) in metricKeys" :key="key">
          <q-separator v-if="index > 0" vertical class="signal-bar-sep" />
          <signal-metric :metric-key="key" :filters="filters" />
        </template>
      </div>
    </div>

    <q-btn
      flat
      dense
      no-caps
      size="sm"
      color="grey-6"
      class="signal-bar-toggle"
      :icon="hidden ? 'mdi-chart-box-outline' : 'mdi-chevron-right'"
      :label="hidden ? t('signalBar.show') : undefined"
      style="background-color: white"
      @click="setHidden(!hidden)"
    >
      <q-tooltip v-if="!hidden" :delay="400">{{ t('signalBar.hide') }}</q-tooltip>
    </q-btn>
  </div>
</template>

<style scoped>
.signal-bar-embedded {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 1rem;
  margin: 0.5rem 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: #fff;
  border: 1px solid color-mix(in srgb, var(--q-primary) 15%, transparent);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.signal-bar {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 100%;
  min-height: 2rem;
  padding: 0.25rem 0.375rem 0.25rem 0.625rem;
  border-radius: 0.5rem;
  background: #fff;
  border: 1px solid color-mix(in srgb, var(--q-primary) 15%, transparent);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: gap 0.3s ease, padding 0.3s ease;
}
.signal-bar--collapsed {
  gap: 0;
  padding: 0.25rem 0.375rem;
}
.signal-bar-drawer {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  overflow: hidden;
  transition: grid-template-columns 0.5s ease;
}
.signal-bar-drawer--hidden {
  grid-template-columns: minmax(0, 0fr);
  pointer-events: none;
}
.signal-bar-track {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: max-content;
  justify-self: start;
  transition: transform 0.5s ease;
}
.signal-bar:not(.signal-bar--mobile) .signal-bar-drawer--hidden .signal-bar-track {
  transform: translateX(100%);
}
.signal-bar-sep {
  height: 0.875rem;
}
.signal-bar-toggle {
  flex-shrink: 0;
}

.signal-bar--mobile {
  position: relative;
  display: flex;
  flex-direction: column-reverse;
  align-items: stretch;
  max-width: 100%;
  min-width: 0;
}
.signal-bar--mobile:not(.signal-bar--collapsed) {
  width: 100%;
}
.signal-bar--mobile .signal-bar-toggle {
  align-self: flex-end;
}
.signal-bar--mobile:not(.signal-bar--collapsed) .signal-bar-toggle {
  position: absolute;
  top: 0.125rem;
  right: 0.25rem;
  z-index: 1;
}
.signal-bar--mobile .signal-bar-drawer {
  max-width: 100%;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  transition: grid-template 0.4s ease;
}
.signal-bar--mobile .signal-bar-drawer--hidden {
  grid-template-columns: minmax(0, 0fr);
  grid-template-rows: minmax(0, 0fr);
}
.signal-bar--mobile .signal-bar-track {
  width: auto;
  min-width: 0;
  justify-self: stretch;
  flex-wrap: wrap;
  justify-content: flex-start;
  overflow: visible;
}
.signal-bar--mobile .signal-bar-sep {
  display: none;
}
</style>
